import { UploadOutlined } from '@ant-design/icons';
import { Button, message } from 'antd';
import {
  forwardRef,
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
} from 'react';
import Api from '../../../../../api/Api';
import { useSignalR } from '../../../../../utils/hooks';
import styles from '../../../../features/ResultPanel/Style/ValidationPanel.module.scss';

interface UploadInsituProps {
  onUploadComplete: () => void;
  disabled: boolean;
}

const UploadInsitu = forwardRef(
  ({ onUploadComplete, disabled }: UploadInsituProps, ref) => {
    const [loading, setLoading] = useState<boolean>(false);
    const [statusCheckInterval, setStatusCheckInterval] =
      useState<NodeJS.Timeout | null>(null);
    const inputRef = useRef<HTMLInputElement | null>(null);

    const { messageReceived, resetMessageState } = useSignalR(
      'InSituReceiveMessage',
      'InSituUpload:Done'
    );

    useImperativeHandle(ref, () => ({
      click() {
        if (inputRef.current) {
          inputRef.current.click();
        }
      },
    }));

    useEffect(() => {
      return () => {
        if (statusCheckInterval) {
          clearInterval(statusCheckInterval);
        }
      };
    }, [statusCheckInterval]);

    const resetInput = () => {
      if (inputRef.current) {
        inputRef.current.value = '';
      }
    };

    const handleUpload = async (file: File) => {
      setLoading(true);
      try {
        message.info('File upload started');
        const uploadId = await Api.insituPoint.uploadInsituPoint(file);
        if (
          (uploadId?.Result === null || uploadId?.Result === undefined) &&
          uploadId?.Error !== null
        ) {
          message.error({
            content: `${uploadId?.Error?.ErrorMessage}`,
            duration: 5,
          });
        }
      } catch (error) {
        message.error('Failed to upload file');
      } finally {
        resetInput();
        setLoading(false);
      }
    };

    useEffect(() => {
      if (messageReceived) {
        message.success('Upload completed');
        setStatusCheckInterval(null);
        onUploadComplete();
        resetMessageState();
      }
    }, [messageReceived]);

    return (
      <div className={styles.uploadInsituContainer}>
        <input
          id='Validation-NoInsituAvailable-Upload_Insitu_Input'
          type='file'
          className={styles.uploadInsituInput}
          accept='.csv'
          ref={inputRef}
          onChange={(e) => {
            const file = e.target.files?.[0];
            if (file) {
              handleUpload(file);
            }
          }}
        />
        <Button
          id='Validation-NoInsituAvailable-Upload_Insitu_Button'
          data-testid='Validation-NoInsituAvailable-Upload_Insitu_Button'
          icon={<UploadOutlined />}
          className={styles.uploadInsituButton}
          disabled={disabled}
          size='middle'
          type='primary'
          loading={loading}
          onClick={() => {
            if (inputRef.current) {
              inputRef.current.click();
            }
          }}
        >
          Upload in situ data
        </Button>
      </div>
    );
  }
);

UploadInsitu.displayName = 'UploadInsitu';

export default UploadInsitu;
